import React, { ReactNode, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageParams } from '../types/PageParams';
import { Controller, useForm } from 'react-hook-form';
import { SivaContractUpdateDto, SivaPhase } from 'src/api/v2';
import useCompanyContract from '../hooks/use-company-contract';
import { convertIdToNumber } from '../utils/idConversion';
import { useTranslation } from 'react-i18next';
import TextArea from 'src/components/Form/TextArea';
import { Button, ButtonVariant, DateInput, Notification, Theme } from '@innovationnorway/component-library';
import DialogContainer from 'src/components/DialogContainer/DialogContainer';
import { capitalizeFirstLetter } from 'src/utils/string';
import { formatDateToNOString } from 'src/utils/FormatValue';
import { useCalculateSivaKTUDates } from 'src/hooks/use-siva-ktu-date-calculation';
import { useOpenReportingYears } from '../../hooks/use-siva-open-reporting-years';
import { useCluster } from 'src/features/cluster';

type Props = {
  onCancel: () => void;
};

const EndContract = ({ onCancel }: Props) => {
  const { t: tCommon } = useTranslation();
  const { t: tSiva } = useTranslation('siva');

  const { contractId } = useParams<PageParams>();
  const { cluster } = useCluster();

  const { contract, updateSivaContractMutation, currentPhase } = useCompanyContract(
    convertIdToNumber(contractId),
  );
  const { openReportingYears } = useOpenReportingYears(cluster?.id);

  const [validateMinDate, validateMaxDate] = useMemo(() => {
    const today = new Date();

    // Users are allowed to end contracts in the previous reporting year since this does not affect reporting for
    // that year and takes them out of the current reporting year. e.g.
    // We have seen several examples of contracts that were retroactively set to terminate in 2023 during reporting
    // for 2024 (in jan 2025) since the actors had not worked with these companies during 2024 anyways.
    // I know this is confusing, but it is, supposedly, correct.
    const minYear =
      (openReportingYears.length > 0 ? Math.min(...openReportingYears) : today.getFullYear()) - 1;
    const maxYear = openReportingYears.length > 0 ? Math.max(...openReportingYears) : today.getFullYear();

    const minDate = new Date(minYear, 0, 1);
    const maxDate = new Date(maxYear + 1, 0, 1);

    return [minDate, maxDate];
  }, [openReportingYears]);
  const { register, handleSubmit, watch, control } = useForm<SivaContractUpdateDto>({
    defaultValues: {
      ...contract,
      advisorId: contract?.advisorId,
      newPhase: {
        newPhase: SivaPhase.Alumni,
        fromDate: undefined,
      },
    },
  });

  const watchedDate = watch('newPhase.fromDate');

  const [updateError, setUpdateError] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const onClose = () => {
    setUpdateError(false);
  };

  const onSave = handleSubmit((formData) => {
    updateSivaContractMutation
      .mutateAsync(formData)
      .then(() => {
        setUpdateError(false);
        setShowDialog(true);
      })
      .catch(() => {
        setUpdateError(true);
      });
  });

  return (
    <>
      <h2>{tSiva('company.details.contract.endContract')}</h2>
      <form onSubmit={onSave} className="stack">
        <Controller
          control={control}
          name="newPhase.fromDate"
          rules={{
            required: tSiva('newCompany.contract.form.fromDate.required'),
            validate: {
              sameYear: (value) => {
                console.log('validate', value, validateMinDate, validateMaxDate);
                if (!value) {
                  return true;
                }

                const date = new Date(value);
                if (date < validateMinDate || date >= validateMaxDate) {
                  return tSiva('newCompany.contract.form.fromDate.openReportingYear');
                }

                return true;
              },
            },
          }}
          render={({ field: { value, ...field }, fieldState }) => (
            <DateInput
              label={tSiva('company.details.contract.overview.exitDate')}
              className="margin-bottom--2"
              {...field}
              value={value ? new Date(value).toJSON().substring(0, 10) : undefined}
              errorMsg={fieldState.error?.message}
            />
          )}
        />

        {currentPhase?.sivaPhase && currentPhase.sivaPhase !== SivaPhase.Idea && (
          <ExitFormDate endDate={watchedDate} />
        )}

        <TextArea
          {...register('notes')}
          className="height"
          label={tCommon('note')}
          maxLength={500}
          maxCount={500}
        />
        {updateError && (
          <Notification dismissable onClose={onClose} className="width--100" type="error">
            {tSiva('company.details.contract.endContract.error')}
          </Notification>
        )}
        <div className="display--flex gap--1 justify-content--flex-start">
          <Button type="submit">{tSiva('company.details.contract.endContract')}</Button>
          <Button theme={Theme.Neutral} variant={ButtonVariant.Outlined} type="button" onClick={onCancel}>
            {tCommon('cancel')}
          </Button>
        </div>
      </form>
      <DialogContainer show={showDialog} title={tCommon('confirmation')} onClose={onCancel}>
        <p>{tSiva('company.details.contract.endContract.success')}</p>
        <Button onClick={onCancel}>{tCommon('close')}</Button>
      </DialogContainer>
    </>
  );
};

function ExitFormDate({ endDate }: { endDate: Date | undefined }) {
  const { t: tSiva } = useTranslation('siva');
  const [calculatedExitFormDate] = useCalculateSivaKTUDates(endDate);

  const exitFormDateString: ReactNode = useMemo(() => {
    if (!endDate) return 'Utgangsdato er ikke gitt';
    if (!calculatedExitFormDate) return 'Henter dato...';
    const exit = new Date(calculatedExitFormDate);

    // To avoid TimeZone trouble between portal frontend and SL that I do not want to deal with right now
    exit?.setHours(6);

    return capitalizeFirstLetter(formatDateToNOString(exit, true, '{month} {year}', true));
  }, [endDate, calculatedExitFormDate]);

  return (
    <div className="stack-small">
      <p className="font-weight--bold">{tSiva('company.details.contract.overview.dispatchExitForm')}</p>
      <p>{exitFormDateString}</p>
    </div>
  );
}

export default EndContract;
